import { useEffect, useState } from "react";
import { AlertTriangle, DollarSign } from "lucide-react";
import { apiv2 } from "../apiv2";

interface Props {
  projectId: string;
  /**
   * Optional click handler — when provided, the badge becomes a button
   * that navigates the admin to the spend dashboard.
   */
  onClick?: () => void;
}

type Status = {
  total_cost_usd: number;
  effective_cap_usd: number;
  blocked: boolean;
};

/**
 * Inline pill showing the current-month Epoch spend for a project against
 * its effective cap. Returns ``null`` when there's nothing meaningful to
 * show (no Epoch calls this month, or the endpoint errors). Colour-coded
 * by utilization: green <80%, yellow 80-99%, red ≥100% (blocked).
 *
 * Safe to mount on any page given a ``projectId`` — no admin gating here
 * (the endpoint itself is open to project members for read).
 */
export default function EpochSpendBadge({ projectId, onClick }: Props) {
  const [status, setStatus] = useState<Status | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await apiv2.getProjectEpochSpend(projectId);
        if (!cancelled) setStatus(s);
      } catch {
        // Endpoint may 404 for projects not yet using Epoch, or the
        // backend may be unreachable. Either way we just hide the badge
        // rather than blow up the project page.
        if (!cancelled) setStatus(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (!status) return null;
  // Hide when the project has had zero Epoch spend this month — the
  // badge is only useful when there's real activity to surface.
  if (status.total_cost_usd <= 0 && !status.blocked) return null;

  const pct =
    status.effective_cap_usd > 0
      ? Math.min(100, (status.total_cost_usd / status.effective_cap_usd) * 100)
      : 0;

  let tone = "text-green-400/80 bg-green-500/10 border-green-500/20";
  if (status.blocked) {
    tone = "text-red-400 bg-red-500/10 border-red-500/20";
  } else if (pct >= 80) {
    tone = "text-yellow-400/90 bg-yellow-500/10 border-yellow-500/20";
  }

  const title = status.blocked
    ? `Epoch spend cap reached: $${status.total_cost_usd.toFixed(
        2,
      )} / $${status.effective_cap_usd.toFixed(2)} this month. New LLM calls are blocked.`
    : `Epoch spend this month: $${status.total_cost_usd.toFixed(
        2,
      )} / $${status.effective_cap_usd.toFixed(2)} (${pct.toFixed(0)}%)`;

  const inner = (
    <>
      {status.blocked ? <AlertTriangle size={9} /> : <DollarSign size={9} />}
      Epoch ${status.total_cost_usd.toFixed(0)} / $
      {status.effective_cap_usd.toFixed(0)}
    </>
  );

  const className = `inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${tone}`;

  if (onClick) {
    return (
      <button
        data-testid="epoch-spend-badge"
        onClick={onClick}
        title={title}
        className={`${className} hover:brightness-125 transition`}
      >
        {inner}
      </button>
    );
  }
  return (
    <span data-testid="epoch-spend-badge" title={title} className={className}>
      {inner}
    </span>
  );
}
